// shared/document-number.js — how INV / QU / CN numbers are read, formatted and
// chosen, for every document kind.
//
// Pure: it works from values the caller already fetched, so one rule applies to
// all three kinds and no runtime can invent its own. In shared/ because the
// server, the demo and the seeders all number documents, and the demo used to
// do it with its own copy of this rule.
//
// THE RULE (chooseNextNumber). A document's number is fixed when it goes out
// (server/document-number.js). The next one is the greatest of three terms:
//
//   the count Sendinvo has given out (document_number_counters, migration 093),
//     which carries across a prefix change and never goes back, so a deleted
//     document's number is never reused;
//   the highest number stored under the current prefix, so a row that got
//     ahead of the count (a typed number, a seed, a deploy's overlap) is
//     followed rather than collided with;
//   the next number the owner chose, to carry on from another app;
//
// plus one, stepping past any number already taken whatever its case.
//
// NOBODY TYPES A NUMBER, AND A DRAFT HAS NONE (ADR-026). What a draft shows is
// the number it is EXPECTED to take, the answer to the rule above worked out
// fresh each time, and every draft of a kind expects the same one. When nothing
// is known (no connection, a schedule's future invoice) a document says
// "[number]" (NUMBER_PLACEHOLDER): what is known, not which screen it is on.

export const NUMBER_PLACEHOLDER = "[number]";

// The highest next number a person may set: nine digits (migration 093's
// CHECK on companies.<kind>_next_number).
export const MAX_SEQUENCE = 999999999;

// How many digits a STORED number is read to. More than a person may set,
// because the rule this replaced read a number of any length: a company that
// brought a longer scheme over from another app (INV-2025090001) goes on from
// it rather than starting again at INV-0001. Fifteen is still exact in a
// JavaScript number and in a BIGINT.
export const MAX_STORED_DIGITS = 15;
const STORED_DIGITS = new RegExp(`^[0-9]{1,${MAX_STORED_DIGITS}}$`);

// Trim the way the migration's SQL does (btrim with the same characters), so
// the backfill and this module read one stored number the same way.
function trimNumber(value) {
  return String(value ?? "").replace(/^[ \t\r\n]+|[ \t\r\n]+$/g, "");
}

function pad4(sequence) {
  return String(sequence).padStart(4, "0");
}

/** "INV" and 457 → "INV-0457"; a blank prefix is the digits alone. Past 9999 the number prints in full. */
export function formatDocumentNumber(prefix, sequence) {
  const clean = trimNumber(prefix);
  return clean ? `${clean}-${pad4(sequence)}` : pad4(sequence);
}

/**
 * The sequence a stored number carries under a prefix, or null when it isn't
 * one of that prefix's numbers: "inv-0457" under "INV" is 457, "SP-0458" under
 * "INV" is null, "0457" under a blank prefix is 457. One to fifteen digits,
 * after exactly one hyphen; the prefix compared case-insensitively and never
 * read as a pattern, so "A.B" or "INV2026" are just text. Mirrors
 * pg_temp.document_sequence in db/migrations/093_document_numbers_at_send.sql
 * character for character; test/db/migration-093.test.mjs holds the two to
 * one fixture list.
 */
export function sequenceUnderPrefix(number, prefix) {
  const num = trimNumber(number);
  const pre = trimNumber(prefix);
  let digits;
  if (!pre) {
    digits = num;
  } else {
    const head = num.slice(0, pre.length + 1);
    if (head.toUpperCase() !== `${pre.toUpperCase()}-`) return null;
    digits = num.slice(pre.length + 1);
  }
  if (!STORED_DIGITS.test(digits)) return null;
  return Number.parseInt(digits, 10);
}

/**
 * The next number of a kind, and what was last given out.
 *
 *   prefix            the company's current prefix for the kind.
 *   chosenNext        the owner's next-number setting, or null to follow on.
 *   lastIssued        the stored count (0 when there is none).
 *   lastIssuedNumber  that count's number as it was printed, or null.
 *   numbers           every stored, non-null number of the kind in the company,
 *                     drafts included: all of them are taken.
 *
 * Returns { sequence, number, lastSequence, lastNumber }. `lastNumber` is the
 * number the refusal of a too-low setting names: the row's own text when the
 * rows are ahead of the count, else the count's printed number, so after a
 * change from INV to SP it still names INV-0457 rather than an SP-0457 nobody
 * was ever sent.
 */
export function chooseNextNumber({ prefix, chosenNext = null, lastIssued = 0, lastIssuedNumber = null, numbers = [] } = {}) {
  let rowsMax = 0;
  let rowsMaxText = null;
  const taken = new Set();
  for (const raw of numbers || []) {
    const text = trimNumber(raw);
    if (!text) continue;
    taken.add(text.toUpperCase());
    const seq = sequenceUnderPrefix(text, prefix);
    if (seq != null && seq > rowsMax) {
      rowsMax = seq;
      rowsMaxText = text;
    }
  }
  const counted = Math.max(0, Number.parseInt(String(lastIssued ?? 0), 10) || 0);
  const lastSequence = Math.max(counted, rowsMax);
  let lastNumber;
  if (rowsMax > counted) lastNumber = rowsMaxText;
  else lastNumber = trimNumber(lastIssuedNumber) || (rowsMax === lastSequence ? rowsMaxText : null);
  if (!lastNumber && lastSequence > 0) lastNumber = formatDocumentNumber(prefix, lastSequence);

  const chosen = Math.max(0, Number.parseInt(String(chosenNext ?? 0), 10) || 0);
  let sequence = Math.max(lastSequence + 1, chosen);
  while (taken.has(formatDocumentNumber(prefix, sequence).toUpperCase())) sequence += 1;
  return { sequence, number: formatDocumentNumber(prefix, sequence), lastSequence, lastNumber: lastNumber || null };
}

/**
 * The highest stored number under each prefix a kind's numbers were given
 * under, one per prefix whatever its case: ["INV-0041", "SP-0003"]. With the
 * count, it is everything chooseNextNumber needs to say what the next number
 * would be under ANY prefix (`expectedUnderPrefix`), including one the company
 * used before and might go back to, whose own numbers may be ahead of the
 * count: before migration 093 a prefix change started again at 0001, and the
 * backfill counted the current prefix only. A number belongs to the one prefix
 * sequenceUnderPrefix reads it under, which is the digits alone or everything
 * before its last hyphen; one that reads under none is left out.
 */
export function highestNumberUnderEachPrefix(numbers) {
  const highest = new Map();
  for (const raw of numbers || []) {
    const text = trimNumber(raw);
    if (!text) continue;
    const hyphen = text.lastIndexOf("-");
    const prefix = STORED_DIGITS.test(text) ? "" : hyphen > 0 ? text.slice(0, hyphen) : null;
    if (prefix == null || prefix !== trimNumber(prefix)) continue;
    const sequence = sequenceUnderPrefix(text, prefix);
    if (sequence == null) continue;
    const key = prefix.toUpperCase();
    if (!highest.has(key) || sequence > highest.get(key).sequence) highest.set(key, { sequence, text });
  }
  return [...highest.values()].map((entry) => entry.text);
}

/**
 * What the next number would be under a prefix, from one kind's entry of the
 * `numbering` block the server serves (server/document-number.js,
 * readDocumentNumbering): chooseNextNumber itself, fed the count and the
 * highest number under each prefix rather than every stored number. The
 * answer is the same, because no number already taken under a prefix is above
 * the highest one read under it. For a prefix field that says, as it's typed,
 * where numbering would go on from. Null when the entry doesn't carry the
 * count and the highest numbers (an API from before it served them), so
 * nothing is promised that the save might not keep.
 */
export function expectedUnderPrefix(entry, prefix) {
  if (!entry || !Array.isArray(entry.highestUnderEachPrefix) || entry.lastIssued == null) return null;
  return chooseNextNumber({
    prefix,
    chosenNext: entry.chosenNext ?? null,
    lastIssued: entry.lastIssued,
    numbers: entry.highestUnderEachPrefix
  });
}

const KIND_WORDS = { invoice: "invoice", quote: "quote", credit_note: "credit note", creditNote: "credit note" };

/**
 * What the next-number setting says under its field once the value is good,
 * and a prefix field as it's typed: "Your next invoice will be INV-0500."
 */
export function nextNumberSentence(kind, number) {
  return `Your next ${KIND_WORDS[kind] || "invoice"} will be ${number}.`;
}

/**
 * Whether a typed next number can be saved: { ok, sequence, hint, refusal }.
 * Forward only — it must be above the last number given out — and at most nine
 * digits. `hint` is what the field says under it once the value is good.
 */
export function checkNextNumberSetting({ kind = "invoice", prefix = "", lastSequence = 0, lastNumber = null, typed } = {}) {
  const refuse = (refusal) => ({ ok: false, sequence: null, hint: null, refusal });
  const text = String(typed ?? "").trim();
  if (!/^[0-9]+$/.test(text)) return refuse("Enter a whole number, like 500.");
  const significant = text.replace(/^0+/, "");
  if (!significant) return refuse("Enter a whole number, like 500.");
  if (significant.length > 9) return refuse("Enter a number of 9 digits or fewer.");
  const sequence = Number.parseInt(significant, 10);
  const last = Math.max(0, Number.parseInt(String(lastSequence ?? 0), 10) || 0);
  if (sequence <= last) {
    const named = trimNumber(lastNumber) || formatDocumentNumber(prefix, last);
    return refuse(`Enter a number higher than ${named}, the last one Sendinvo gave out.`);
  }
  return { ok: true, sequence, hint: nextNumberSentence(kind, formatDocumentNumber(prefix, sequence)), refusal: null };
}

/**
 * The one display rule for a document's number ON ITSELF: the number it holds,
 * else the one it's expected to take, else the caller's fallback — the
 * placeholder on the document, "Draft" as its own screen's title. Never a
 * made-up number. A list doesn't use it: see listedDocumentNumber.
 */
export function shownDocumentNumber(held, expected, fallback = NUMBER_PLACEHOLDER) {
  return trimNumber(held) || trimNumber(expected) || fallback;
}

// What a list calls a draft that holds no number.
export const LISTED_DRAFT = "Draft";

/**
 * What a document is called in a LIST, its search and a row's menu (#892): the
 * number it holds, else "Draft" for a draft, else nothing. Never the number a
 * draft expects. Every draft of a kind expects the same one, so four drafts
 * read as one invoice listed four times, and only one of them can take it. The
 * expected number belongs on the draft itself: its compose screen, preview,
 * PDF and detail (shownDocumentNumber). A draft a send fixed a number on holds
 * it, and shows it here: that one is its own.
 */
export function listedDocumentNumber(held, status = "draft") {
  const holds = trimNumber(held);
  if (holds) return holds;
  return isDraftStatus(status) ? LISTED_DRAFT : "";
}

// Sorts after every text documentNumberSortText gives: those start with the
// number zero-padded to MAX_STORED_DIGITS + 1 digits, or a space.
export const ABOVE_EVERY_NUMBER = "9".repeat(MAX_STORED_DIGITS + 2);

/**
 * What a list orders a row by when it sorts by number. A draft holding no
 * number sorts where the number it takes will be, above every number given
 * out, because the count only moves forward: at the top when the newest come
 * first, as it did when it showed its expected number. Drafts tie with each
 * other and keep the order they came in, newest first. The app's
 * `DocumentNumber.sortText` is the same rule.
 */
export function listedNumberSortText(held, status = "draft") {
  if (!trimNumber(held) && isDraftStatus(status)) return ABOVE_EVERY_NUMBER;
  return documentNumberSortText(held);
}

function isDraftStatus(status) {
  return String(status || "draft").toLowerCase() === "draft";
}

/**
 * What a list sorts a number by: its trailing digits, so INV-0457 comes before
 * SP-0458 now that the count carries across a prefix change. Null when there
 * are none (a placeholder, "Draft"); callers break ties on the text.
 */
export function documentNumberSortKey(text) {
  const match = /([0-9]+)[ \t\r\n]*$/.exec(String(text ?? ""));
  return match ? Number.parseInt(match[1], 10) : null;
}

/**
 * The same order as ONE comparable string, for a list that sorts by a single
 * value per row (the web's `sortRows`): the trailing digits zero-padded, so
 * text order is number order, then the number itself, lowercased, to break a
 * tie between two prefixes. Rows showing the same number compare equal and
 * keep the order they came in, which is newest first. Null when there is no
 * number to sort by.
 */
export function documentNumberSortText(text) {
  const clean = trimNumber(text);
  if (!clean) return null;
  const key = documentNumberSortKey(clean);
  return `${key == null ? "" : String(key).padStart(MAX_STORED_DIGITS + 1, "0")} ${clean.toLowerCase()}`;
}

// A SENT INVOICE OR CREDIT NOTE IS NEVER DELETED (#927, ADR-026). Only a draft
// holding no number may go. One that went out is voided, or corrected with a
// credit note or a replacement, and keeps its number on file, so the numbers
// given out read as one unbroken run. So does a draft a send fixed a number on
// and then failed: the client may already hold that number. A quote is not a
// tax document, so any quote may still be deleted. The server refuses by this
// rule, the demo answers by it, and the web offers Delete only where it holds;
// the app's `DocumentActions` is the same rule in Swift.
const KEPT_KINDS = new Set(["invoice", "creditNote", "credit_note"]);

/** Whether a document may be deleted: any quote, or a draft holding no number. */
export function mayDeleteDocument(kind, { status, number } = {}) {
  if (!KEPT_KINDS.has(kind)) return true;
  return isDraftStatus(status) && !trimNumber(number);
}

/**
 * The refusal when it may not, or null when it may. A draft that kept its
 * number is named by it, because that number is why; a sent one by its kind.
 */
export function keptOnFileSentence(kind, { status, number } = {}) {
  if (mayDeleteDocument(kind, { status, number })) return null;
  const invoice = kind === "invoice";
  if (isDraftStatus(status)) {
    return `This draft kept ${trimNumber(number)} from a send that didn’t finish, so it can’t be deleted. Send it or void it instead.`;
  }
  return invoice
    ? "Sent invoices stay on file, so this one can’t be deleted. To cancel it, void it or issue a credit note."
    : "Sent credit notes stay on file, so this one can’t be deleted. To cancel it, void it.";
}

// The next number from the rows alone: no stored count, no chosen number.
// `field` is the row column to read ("invoice_number", "quote_number" or
// "credit_note_number"). Numbers under another prefix are not counted, so on
// its own this restarts at 0001 after a prefix change; the count the server
// keeps is what carries it on. Kept for the review seeder's lifecycle extras,
// which number the documents it writes as sent.
export function getNextDocumentNumberFromRows(rows, prefix, field) {
  return chooseNextNumber({ prefix, numbers: (rows || []).map((row) => row?.[field]) }).number;
}

export function getNextInvoiceNumberFromRows(rows, prefix = "INV") {
  return getNextDocumentNumberFromRows(rows, prefix, "invoice_number");
}

export function getNextQuoteNumberFromRows(rows, prefix = "QU") {
  return getNextDocumentNumberFromRows(rows, prefix, "quote_number");
}

export function getNextCreditNoteNumberFromRows(rows, prefix = "CN") {
  return getNextDocumentNumberFromRows(rows, prefix, "credit_note_number");
}
